import * as Sentry from '@sentry/react';
import { message } from 'antd';
import axios from 'axios';

import { LOCAL_STORAGE } from '../constants/local-storage.constant';
import { getSubdomain } from '../helpers/getSubdomain';
import { getTokenFromUrl } from '../helpers/getTokenFromUrl';
import { isNetworkError, getNetworkErrorMessage } from '../helpers/networkUtils';

export const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/auth`,
  timeout: 30000, // Reduced from 180s to 30s for better UX
  headers: {
    'X-Tenant': getSubdomain(),
    Authorization: `Bearer ${
      localStorage.getItem(LOCAL_STORAGE.CUSTOMER_API_TOKEN) ||
      getTokenFromUrl()
    }`,
  },
});

// Public API — no Authorization header, used for unauthenticated pages
export const publicApi = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/auth`,
  timeout: 30000,
  headers: {
    'X-Tenant': getSubdomain(),
  },
});

// Rotas centrais (fora do /auth), como o código do segundo fator. Sem os
// interceptors do api: a tela do código mostra os erros do jeito dela.
export const centralApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 30000,
  headers: {
    Accept: 'application/json',
  },
});

// Create a separate instance for file downloads with 20 second timeout
export const fileApi = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/auth`,
  timeout: 20000, // 20 seconds for file downloads
  headers: {
    'X-Tenant': getSubdomain(),
    Authorization: `Bearer ${
      localStorage.getItem(LOCAL_STORAGE.CUSTOMER_API_TOKEN) ||
      getTokenFromUrl()
    }`,
  },
});

api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    const successStatus = response?.status;
    const successMessage = response?.data?.message;

    message.destroy();
    if ((successStatus === 200 || successStatus === 201) && successMessage) {
      message.success(response.data.message);
    }

    return response;
  },
  (error) => {
    message.destroy();

    const errorStatus = error.response?.status;
    const errorMessage = error.response?.data?.message;

    // Log error to Sentry with network error context
    Sentry.withScope((scope) => {
      scope.setTag('isNetworkError', isNetworkError(error));
      scope.setTag('errorCode', error.code || 'unknown');
      scope.setExtra('status', errorStatus);
      scope.setExtra('message', errorMessage);
      scope.setExtra('url', error.config?.url);
      scope.setExtra('method', error.config?.method);
      scope.setExtra('requestData', error.config?.data);
      scope.setExtra('responseData', error.response?.data);
      scope.setExtra('timeout', error.config?.timeout);
      Sentry.captureException(error);
    });

    // Handle network errors specifically
    if (isNetworkError(error)) {
      const networkMessage = getNetworkErrorMessage(error);
      message.error(networkMessage);
    } else if (errorStatus !== 500 && errorMessage) {
      message.error(errorMessage);
    } else {
      message.error('Não foi possível concluir sua solicitação!');
    }

    return Promise.reject(error);
  }
);

// File API interceptors - similar but with different messaging for downloads
fileApi.interceptors.request.use(
  (config) => {
    // Update authorization header if it has changed
    config.headers.Authorization = `Bearer ${
      localStorage.getItem(LOCAL_STORAGE.CUSTOMER_API_TOKEN) ||
      getTokenFromUrl()
    }`;
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

fileApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Log file download errors to Sentry
    Sentry.withScope((scope) => {
      scope.setTag('component', 'fileApi');
      scope.setTag('isNetworkError', isNetworkError(error));
      scope.setTag('errorCode', error.code || 'unknown');
      scope.setExtra('status', error.response?.status);
      scope.setExtra('url', error.config?.url);
      scope.setExtra('method', error.config?.method);
      scope.setExtra('timeout', error.config?.timeout);
      scope.setExtra('responseType', error.config?.responseType);
      Sentry.captureException(error);
    });

    return Promise.reject(error);
  }
);

message.config({
  maxCount: 1,
});

export const getUploadProps = (actionUrl: string) => ({
  name: 'file',
  multiple: true,
  action: `${process.env.REACT_APP_API_URL}/auth/${actionUrl}`,
  headers: {
    'X-Tenant': getSubdomain(),
    Authorization: `Bearer ${
      localStorage.getItem(LOCAL_STORAGE.CUSTOMER_API_TOKEN) ||
      getTokenFromUrl()
    }`,
  },
  onChange: (uploadInfo: any) => {
    const { status, name } = uploadInfo.file;
    if (status === 'done') {
      message.success(`${name} arquivo enviado com sucesso.`);
    } else if (status === 'error') {
      message.error(`${name} falha no envio do arquivo.`);
    }
  },
  onDrop: () => {},
});
